import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {
  fetchClasses,
  createPaymentIntent,
  bulkRegisterForClass,
} from "../api/apiClient";

const STEPS = [
  "Select Class & Members",
  "Prerequisites",
  "Terms & Conditions",
  "Payment & Confirm",
];

/**
 * BulkRegistrationWizard — 4-step dialog for agency admin bulk enrollment.
 *
 * Steps:
 *  0: Select class from tenant class list + pick members via checkbox table
 *  1: Prerequisites — confirm all selected members meet requirements
 *  2: Terms & Conditions — accept on behalf of all members
 *  3: Payment & Confirm — shows total fee (per-member × count), submits
 *
 * Props:
 *  open               {boolean}
 *  onClose            {function}
 *  onComplete         {function(results)} — called with bulk registration results
 *  members            {array}  — full agency member list, each with id, name/username, category
 *  preselectedMembers {array}  — subset of members to pre-check (empty = none pre-checked)
 *  agencyId           {number}
 *  tenantId           {string}
 */
export default function BulkRegistrationWizard({
  open,
  onClose,
  onComplete,
  members = [],
  preselectedMembers = [],
  agencyId,
  tenantId,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const [activeStep, setActiveStep] = useState(0);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [prerequisitesConfirmed, setPrerequisitesConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Reset all state when dialog opens; capture preselectedMembers at that moment
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedClassId("");
      setSelectedClass(null);
      setSelectedMemberIds(new Set(preselectedMembers.map((m) => m.id)));
      setPrerequisitesConfirmed(false);
      setTermsAccepted(false);
      setPaymentIntent(null);
      setPaymentError("");
      setSubmitError("");
    }
  }, [open, preselectedMembers]);

  // Load tenant classes when dialog opens
  useEffect(() => {
    if (!open) return;
    setClassesLoading(true);
    fetchClasses(tenantId)
      .then((cls) => setClasses(Array.isArray(cls) ? cls : []))
      .catch(() => setClasses([]))
      .finally(() => setClassesLoading(false));
  }, [open, tenantId]);

  // Sync selectedClass + auto-confirm prereqs when class changes
  useEffect(() => {
    if (!selectedClassId) {
      setSelectedClass(null);
      setPrerequisitesConfirmed(false);
      return;
    }
    const cls = classes.find((c) => String(c.id) === String(selectedClassId));
    setSelectedClass(cls || null);
    const hasPrereqs =
      cls?.prereqs &&
      cls.prereqs.trim() !== "" &&
      cls.prereqs.toLowerCase() !== "none";
    setPrerequisitesConfirmed(!hasPrereqs);
  }, [selectedClassId, classes]);

  const fetchPaymentIntentForClass = useCallback(async () => {
    if (!selectedClassId) return;
    setPaymentLoading(true);
    setPaymentError("");
    try {
      const intent = await createPaymentIntent(selectedClassId);
      setPaymentIntent(intent);
    } catch {
      setPaymentError("Could not load payment information. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  }, [selectedClassId]);

  const handleNext = async () => {
    if (activeStep === 2) {
      await fetchPaymentIntentForClass();
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((prev) => prev - 1);
  };

  const handleToggleMember = (memberId) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      next.has(memberId) ? next.delete(memberId) : next.add(memberId);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedMemberIds.size === members.length) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(members.map((m) => m.id)));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const userIds = Array.from(selectedMemberIds);
      const wizardData = {
        prerequisitesConfirmed,
        termsAccepted,
        paymentIntentId: paymentIntent?.paymentIntentId || null,
      };
      const results = await bulkRegisterForClass(
        selectedClassId,
        userIds,
        agencyId,
        wizardData,
      );
      if (onComplete) onComplete(results);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Registration failed. Please try again.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selectedMemberIds.size;
  const classPrereqs = selectedClass?.prereqs?.trim();
  const hasPrereqs =
    classPrereqs &&
    classPrereqs.toLowerCase() !== "none" &&
    classPrereqs !== "";

  const paymentRequired = paymentIntent?.required === true;
  const paymentAmount = paymentIntent?.amount ?? 0;
  const currency = (paymentIntent?.currency || "usd").toUpperCase();
  const totalAmount =
    typeof paymentAmount === "number" ? paymentAmount * selectedCount : 0;

  // ── Step 0: Class selection + member multi-select ─────────────────────────
  const renderSelectionStep = () => (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel>Class</InputLabel>
        <Select
          value={selectedClassId}
          label="Class"
          onChange={(e) => setSelectedClassId(e.target.value)}
          disabled={classesLoading}
        >
          {classesLoading ? (
            <MenuItem value="">
              <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
            </MenuItem>
          ) : classes.length === 0 ? (
            <MenuItem value="" disabled>
              No classes available
            </MenuItem>
          ) : (
            classes.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontFamily: "var(--font-display)" }}>
        Select Members ({selectedCount} selected)
      </Typography>
      <TableContainer
        sx={{ maxHeight: 280, border: "1px solid #e0e0e0", borderRadius: 1 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: "action.hover" }}>
                <Checkbox
                  checked={
                    members.length > 0 &&
                    selectedMemberIds.size === members.length
                  }
                  indeterminate={
                    selectedMemberIds.size > 0 &&
                    selectedMemberIds.size < members.length
                  }
                  onChange={handleToggleAll}
                />
              </TableCell>
              <TableCell sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                Name
              </TableCell>
              <TableCell sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                Category
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m) => (
              <TableRow
                key={m.id}
                hover
                onClick={() => handleToggleMember(m.id)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedMemberIds.has(m.id)}
                    onChange={() => handleToggleMember(m.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableCell>
                <TableCell>{m.name || m.fullName || m.username}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {m.category || "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {members.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="caption" color="text.secondary">
                    No agency members found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ── Step 1: Prerequisites ─────────────────────────────────────────────────
  const renderPrerequisitesStep = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
        Prerequisites for <strong>{selectedClass?.title}</strong>
      </Typography>
      {hasPrereqs ? (
        <Box
          sx={{
            bgcolor: "var(--color-surface-2)",
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            border: "1px solid var(--color-hairline-cloud)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "var(--color-ink-2)", whiteSpace: "pre-wrap" }}
          >
            {classPrereqs}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: "var(--color-success-bg)",
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            border: "1px solid var(--color-success-bg)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#166534" }}>
            No prerequisites are required for this class.
          </Typography>
        </Box>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={prerequisitesConfirmed}
            onChange={(e) => setPrerequisitesConfirmed(e.target.checked)}
            disabled={!hasPrereqs}
            sx={{ color: tPrimary, "&.Mui-checked": { color: tPrimary } }}
          />
        }
        label={
          hasPrereqs
            ? `I confirm all ${selectedCount} selected member(s) meet the listed prerequisites.`
            : "No prerequisites required — you may proceed."
        }
        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
      />
    </Box>
  );

  // ── Step 2: Terms & Conditions ────────────────────────────────────────────
  const renderTermsStep = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
        Terms & Conditions
      </Typography>
      <Box
        sx={{
          bgcolor: "var(--color-surface-2)",
          borderRadius: 2,
          p: 2,
          mb: 2.5,
          border: "1px solid var(--color-hairline-cloud)",
          maxHeight: 200,
          overflowY: "auto",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "var(--color-ink-2)", whiteSpace: "pre-wrap", lineHeight: 1.7 }}
        >
          By registering these members, you confirm that they agree to attend
          this course and comply with all class policies and rules set by the
          training center.
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            sx={{ color: tPrimary, "&.Mui-checked": { color: tPrimary } }}
          />
        }
        label="I accept the Terms & Conditions on behalf of all selected members."
        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
      />
    </Box>
  );

  // ── Step 3: Payment & Confirm ─────────────────────────────────────────────
  const renderPaymentStep = () => {
    if (paymentLoading) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={4}
          gap={2}
        >
          <CircularProgress size={32} sx={{ color: tPrimary }} />
          <Typography color="text.secondary">
            Loading payment information…
          </Typography>
        </Box>
      );
    }
    if (paymentError) {
      return <Alert severity="error">{paymentError}</Alert>;
    }
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
          Enrollment Summary
        </Typography>
        <Box
          sx={{
            bgcolor: "var(--color-surface-2)",
            borderRadius: 2,
            p: 2,
            mb: 2,
            border: "1px solid var(--color-hairline-cloud)",
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Class
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {selectedClass?.title}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Members
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {selectedCount}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Total Fee
            </Typography>
            {paymentRequired ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                <AttachMoneyIcon sx={{ fontSize: 18, color: tPrimary }} />
                <Typography variant="body2" fontWeight={700} color={tPrimary}>
                  {totalAmount.toFixed(2)} {currency}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({Number(paymentAmount).toFixed(2)} × {selectedCount})
                </Typography>
              </Box>
            ) : (
              <Chip
                label="Free"
                color="success"
                size="small"
                icon={<CheckCircleOutlineIcon />}
              />
            )}
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 0.75, fontFamily: "var(--font-display)" }}>
          Selected Members:
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
          {members
            .filter((m) => selectedMemberIds.has(m.id))
            .map((m) => (
              <Chip
                key={m.id}
                label={m.name || m.fullName || m.username}
                size="small"
              />
            ))}
        </Stack>

        {paymentRequired && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Payment of {totalAmount.toFixed(2)} {currency} required for{" "}
            {selectedCount} member(s). Stripe payment integration is pending —
            registration will be processed when Stripe is configured.
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {submitError}
          </Alert>
        )}
      </Box>
    );
  };

  const stepContent = [
    renderSelectionStep(),
    renderPrerequisitesStep(),
    renderTermsStep(),
    renderPaymentStep(),
  ];

  const isNextDisabled =
    (activeStep === 0 && (!selectedClassId || selectedCount === 0)) ||
    (activeStep === 1 && !prerequisitesConfirmed) ||
    (activeStep === 2 && !termsAccepted);

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      PaperProps={{ sx: { borderRadius: 3, maxWidth: 620, width: "100%" } }}
      fullWidth
    >
      <DialogTitle
        sx={{ pb: 1, pt: 3, px: 3, fontWeight: 700, color: tPrimary, fontFamily: "var(--font-display)" }}
      >
        Register Agency Members for Class
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepLabel-label": {
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {stepContent[activeStep]}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="text"
          disabled={submitting}
          sx={{ mr: "auto", color: "var(--color-ink-2)" }}
        >
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={submitting || paymentLoading}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
        )}
        {isLastStep ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || paymentLoading || !!paymentError}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              bgcolor: tPrimary,
              borderRadius: 2,
              "&:hover": { bgcolor: "var(--color-primary)" },
            }}
          >
            {submitting
              ? "Registering…"
              : `Register ${selectedCount} Member${selectedCount !== 1 ? "s" : ""}`}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isNextDisabled}
            sx={{
              bgcolor: tPrimary,
              borderRadius: 2,
              "&:hover": { bgcolor: "var(--color-primary)" },
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
